/*
 * Code generated by Speakeasy (https://speakeasy.com). DO NOT EDIT.
 */

import * as z from "zod/v4-mini";
import { remap as remap$ } from "../../lib/primitives.js";
import { safeParse } from "../../lib/schemas.js";
import { ClosedEnum } from "../../types/enums.js";
import { Result as SafeParseResult } from "../../types/fp.js";
import { SDKValidationError } from "../errors/sdkvalidationerror.js";
import {
  MeterUsageWindow,
  MeterUsageWindow$inboundSchema,
} from "./meterusagewindow.js";
import { SpendProduct, SpendProduct$inboundSchema } from "./spendproduct.js";

/**
 * Whether spend estimates are available for the organization's plan
 */
export const Availability = {
  Available: "available",
  UnsupportedPlan: "unsupported_plan",
} as const;
/**
 * Whether spend estimates are available for the organization's plan
 */
export type Availability = ClosedEnum<typeof Availability>;

export const Currency = {
  Usd: "USD",
} as const;
export type Currency = ClosedEnum<typeof Currency>;

export const PricingBasis = {
  CurrentPaygListPrice: "current_payg_list_price",
} as const;
export type PricingBasis = ClosedEnum<typeof PricingBasis>;

export type SpendBreakdownResponse = {
  /**
   * Whether spend estimates are available for the organization's plan
   */
  availability: Availability;
  /**
   * Trailing twelve billing-cycle date windows
   */
  billingCycles: Array<MeterUsageWindow>;
  currency: Currency;
  pricingBasis: PricingBasis;
  /**
   * The three metered PAYG products in stable display order when available; empty when availability is unsupported_plan
   */
  products: Array<SpendProduct>;
  /**
   * Retrieval timestamp used to distinguish current and future buckets
   */
  queriedAt: Date;
  /**
   * Exact estimated total at current PAYG list prices; zero when availability is unsupported_plan, meaning no estimate was calculated
   */
  totalCostUsd: string;
  window: MeterUsageWindow;
};

/** @internal */
export const Availability$inboundSchema: z.ZodMiniEnum<typeof Availability> = z
  .enum(Availability);

/** @internal */
export const Currency$inboundSchema: z.ZodMiniEnum<typeof Currency> = z.enum(
  Currency,
);

/** @internal */
export const PricingBasis$inboundSchema: z.ZodMiniEnum<typeof PricingBasis> = z
  .enum(PricingBasis);

/** @internal */
export const SpendBreakdownResponse$inboundSchema: z.ZodMiniType<
  SpendBreakdownResponse,
  unknown
> = z.pipe(
  z.object({
    availability: Availability$inboundSchema,
    billing_cycles: z.array(MeterUsageWindow$inboundSchema),
    currency: Currency$inboundSchema,
    pricing_basis: PricingBasis$inboundSchema,
    products: z.array(SpendProduct$inboundSchema),
    queried_at: z.pipe(
      z.iso.datetime({ offset: true }),
      z.transform(v => new Date(v)),
    ),
    total_cost_usd: z.string(),
    window: MeterUsageWindow$inboundSchema,
  }),
  z.transform((v) => {
    return remap$(v, {
      "billing_cycles": "billingCycles",
      "pricing_basis": "pricingBasis",
      "queried_at": "queriedAt",
      "total_cost_usd": "totalCostUsd",
    });
  }),
);

export function spendBreakdownResponseFromJSON(
  jsonString: string,
): SafeParseResult<SpendBreakdownResponse, SDKValidationError> {
  return safeParse(
    jsonString,
    (x) => SpendBreakdownResponse$inboundSchema.parse(JSON.parse(x)),
    `Failed to parse 'SpendBreakdownResponse' from JSON`,
  );
}
